import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog as RDialog, Switch as RSwitch, Tabs as RTabs, Tooltip as RTooltip } from 'radix-ui';
import {
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { SheetResizeHandle } from './sheet-resize-handle.tsx';
import { useSheetWidth } from './sheet-width.ts';

/**
 * Radix returns focus to a `Dialog.Trigger`; these dialogs are opened from ordinary buttons (row actions, switches),
 * so remember the element that had focus when the dialog opened and focus it again on close.
 */
function useFocusReturn() {
  const returnTo = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: () => {
      const active = document.activeElement;
      returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null;
    },
    onCloseAutoFocus: (event: Event) => {
      const target = returnTo.current;
      if (target?.isConnected) {
        event.preventDefault();
        target.focus();
      }
    },
  };
}

const CLOSE_BUTTON =
  'wb-icon-btn -mt-1.5 -mr-1.5 inline-flex size-[36px] shrink-0 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Footer actions (buttons). */
  footer?: ReactNode;
  className?: string;
}

/**
 * Centered dialog for short confirmations that need a reason (kill switch, cancel). It never grows past the
 * viewport: the body scrolls between the fixed header and footer. Radix returns focus to the trigger on close.
 */
export function Dialog({ open, onOpenChange, title, description, children, footer, className }: DialogProps) {
  const focusReturn = useFocusReturn();
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-[var(--wb-overlay)]" />
        <RDialog.Content
          {...focusReturn}
          className={clsx(
            'wb-enter fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-4rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl border border-line bg-surface shadow-elevated',
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-6">
            <div>
              <RDialog.Title className="text-[20px] leading-snug font-semibold tracking-[-0.01em] text-ink">
                {title}
              </RDialog.Title>
              {description ? (
                <RDialog.Description className="wb-prose mt-1 text-ink-2">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{title}</RDialog.Description>
              )}
            </div>
            <RDialog.Close className={CLOSE_BUTTON} aria-label="Close">
              <X className="size-4" aria-hidden />
            </RDialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-6">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 px-6 pb-6">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface DrawerProps extends Omit<DialogProps, 'className'> {
  /** Default CSS width (`34rem`); the user can drag the left edge, and the chosen width is remembered per browser. */
  width?: string;
  /** Key the chosen width is remembered under (`wb.sheetWidth.<key>`); defaults to `width`. */
  resizeKey?: string;
}

/** Right-side sheet for multi-field work next to the list it belongs to (create task, upload). */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = '34rem',
  resizeKey,
}: DrawerProps) {
  const focusReturn = useFocusReturn();
  const sheet = useSheetWidth({ storageKey: resizeKey ?? width, defaultWidth: width, open });
  const closeRef = useRef<HTMLButtonElement>(null);
  // The resize handle is the first tabbable element; open on the close button as before it was added.
  const onOpenAutoFocus = (event: Event) => {
    focusReturn.onOpenAutoFocus();
    if (!closeRef.current) return;
    event.preventDefault();
    closeRef.current.focus();
  };
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-[var(--wb-overlay)]" />
        <RDialog.Content
          {...focusReturn}
          onOpenAutoFocus={onOpenAutoFocus}
          style={{ width: `min(${sheet.width}px, calc(100vw - 1.5rem))` }}
          className="fixed inset-y-3 right-3 z-50 flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-elevated animate-[wb-enter_220ms_cubic-bezier(0.25,1,0.5,1)]"
        >
          <SheetResizeHandle sheet={sheet} label="Resize drawer" />
          <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-2">
            <div>
              <RDialog.Title className="text-[20px] leading-snug font-semibold tracking-[-0.01em] text-ink">
                {title}
              </RDialog.Title>
              {description ? (
                <RDialog.Description className="wb-prose mt-1 text-ink-2">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{title}</RDialog.Description>
              )}
            </div>
            <RDialog.Close ref={closeRef} className={CLOSE_BUTTON} aria-label="Close">
              <X className="size-4" aria-hidden />
            </RDialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-6">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The panel's accessible name (not rendered visibly; put the visible identity in `header`). */
  title: ReactNode;
  children: ReactNode;
  /** Rendered under the eyebrow row, above the scrolling body (identity block, actions). */
  header?: ReactNode;
  /**
   * Default CSS width from `md` up, `25rem`; never wider than the viewport minus the gutters. The user can drag the
   * left edge, and the chosen width is remembered per browser.
   */
  width?: string;
  /** Key the chosen width is remembered under (`wb.sheetWidth.<key>`); defaults to `width`. */
  resizeKey?: string;
  /** Small upper-case eyebrow text left of the close button. */
  label?: ReactNode;
}

/** True when the page is handling a user gesture now (click, key) or focus sits on a control the user used. */
function openedByUser(active: Element | null): boolean {
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  if (activation?.isActive) return true;
  return active instanceof HTMLElement && active !== document.body;
}

/**
 * Non-modal right-side sheet for master-detail pages (select a row, read its details next to the list). Unlike
 * `Drawer` it renders no overlay, traps no focus and is a `complementary` landmark, not a dialog: the page behind
 * it stays usable, and clicking another row just swaps its content. It sits at `z-30`, under `Drawer` and `Dialog`.
 * Opened by a user action, it moves focus to its close button; on close it returns focus to the opener when focus
 * was inside the panel. Escape closes it only while focus is inside. Below `md` it fills the viewport.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  children,
  header,
  width = '25rem',
  label,
  resizeKey,
}: SidePanelProps) {
  const titleId = useId();
  const sheet = useSheetWidth({ storageKey: resizeKey ?? width, defaultWidth: width, open });
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const focusInside = useRef(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      const active = document.activeElement;
      returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null;
      if (openedByUser(active)) closeRef.current?.focus();
    }
    if (!open && wasOpen.current) {
      const target = returnTo.current;
      const active = document.activeElement;
      // Return focus only if it was in the panel (now unmounted), never steal it from where the user went.
      const lost = !active || active === document.body || !active.isConnected;
      if (focusInside.current && lost && target?.isConnected) target.focus();
      focusInside.current = false;
      returnTo.current = null;
    }
    wasOpen.current = open;
  }, [open]);

  if (!open) return null;

  const onFocus = (event: FocusEvent<HTMLElement>) => {
    focusInside.current = true;
    // Entering from a row other than the opener: return there on close.
    const from = event.relatedTarget;
    if (from instanceof HTMLElement && !panelRef.current?.contains(from)) returnTo.current = from;
  };
  const onBlur = (event: FocusEvent<HTMLElement>) => {
    const to = event.relatedTarget;
    if (!(to instanceof Node && panelRef.current?.contains(to))) focusInside.current = false;
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    event.stopPropagation();
    onOpenChange(false);
  };

  return createPortal(
    <aside
      ref={panelRef}
      aria-labelledby={titleId}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      style={{ '--wb-side-panel-w': `min(${sheet.width}px, calc(100vw - 1.5rem))` } as CSSProperties}
      className="fixed inset-3 z-30 flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-elevated animate-[wb-enter_220ms_cubic-bezier(0.25,1,0.5,1)] md:left-auto md:w-[var(--wb-side-panel-w)]"
    >
      <SheetResizeHandle sheet={sheet} label="Resize panel" />
      <span id={titleId} className="sr-only">
        {title}
      </span>
      <div className="flex flex-col gap-3 border-b border-line px-5 pt-4 pb-4">
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">{label}</span>
          <button
            ref={closeRef}
            type="button"
            className={clsx(CLOSE_BUTTON, 'mt-0 mr-0')}
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        {header}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-6">{children}</div>
    </aside>,
    document.body,
  );
}

export interface TabItem {
  value: string;
  label: ReactNode;
  /** Small count or badge after the label. */
  meta?: ReactNode;
  content: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  className?: string;
}

/** Tracks whether a horizontal scroller has hidden content on either side. */
function useOverflow() {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [update]);
  const scrollBy = (direction: -1 | 1) => {
    const el = ref.current;
    el?.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' });
  };
  return { ref, edges, update, scrollBy };
}

const TAB_ARROW =
  'absolute top-0 z-10 hidden h-[42px] w-[36px] items-center justify-center text-ink-2 hover:text-ink [@media(pointer:fine)]:inline-flex';

/**
 * Underlined tabs (Radix: arrow-key navigation, roving focus). When the strip is wider than its column, a fade
 * and arrow buttons show that more tabs exist; keyboard users reach them with the arrow keys.
 */
export function Tabs({ items, value, onValueChange, label, className }: TabsProps) {
  const overflow = useOverflow();
  const { update } = overflow;
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the tab set changes
  useEffect(update, [items.length, update]);
  return (
    <RTabs.Root
      value={value}
      onValueChange={onValueChange}
      className={clsx('flex min-w-0 flex-col', className)}
    >
      <div className="relative min-w-0">
        {overflow.edges.start && (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-12 bg-gradient-to-r from-surface-2 to-transparent"
            />
            <button
              type="button"
              tabIndex={-1}
              aria-hidden
              className={clsx(TAB_ARROW, 'left-0 justify-start')}
              onClick={() => overflow.scrollBy(-1)}
            >
              <ChevronLeft className="size-4" />
            </button>
          </>
        )}
        <RTabs.List
          ref={overflow.ref}
          aria-label={label}
          className="flex w-fit max-w-full gap-0.5 overflow-x-auto overflow-y-hidden rounded-full bg-surface-2 p-1 [scrollbar-width:none]"
        >
          {items.map((item) => (
            <RTabs.Trigger
              key={item.value}
              value={item.value}
              onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' })}
              className="inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13.5px] font-medium text-ink-3 transition-[color,background-color,box-shadow] duration-150 hover:text-ink data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-card"
            >
              {item.label}
              {item.meta !== undefined && (
                <span className="rounded-full bg-surface-3 px-1.5 font-mono text-[12px] leading-[18px] text-ink-2">
                  {item.meta}
                </span>
              )}
            </RTabs.Trigger>
          ))}
        </RTabs.List>
        {overflow.edges.end && (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-12 bg-gradient-to-l from-surface-2 to-transparent"
            />
            <button
              type="button"
              tabIndex={-1}
              aria-hidden
              className={clsx(TAB_ARROW, 'right-0 justify-end')}
              onClick={() => overflow.scrollBy(1)}
            >
              <ChevronRight className="size-4" />
            </button>
          </>
        )}
      </div>
      {items.map((item) => (
        <RTabs.Content
          key={item.value}
          value={item.value}
          className="min-w-0 pt-5 focus-visible:outline-none"
        >
          {item.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Accessible name. When `visibleLabel` is set, keep its text inside this name (WCAG 2.5.3). */
  label: string;
  disabled?: boolean;
  /** Use the enforcement accent when on (kill switch engaged). */
  enforce?: boolean;
  /** Id of the switch button; needed for an external `<label htmlFor>`. */
  id?: string;
  /** Text shown next to the switch; clicking it toggles the switch. */
  visibleLabel?: ReactNode;
  /** Id(s) of helper text that describes the effect. */
  describedBy?: string;
}

/**
 * Accessible toggle (role=switch). `label` is the accessible name. 20px visual with a 36px hit area; an optional
 * visible label toggles it too.
 */
export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
  enforce,
  id,
  visibleLabel,
  describedBy,
}: SwitchProps) {
  const autoId = useId();
  const switchId = id ?? autoId;
  const root = (
    <RSwitch.Root
      id={switchId}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      aria-describedby={describedBy}
      className={clsx(
        // 36x20 visual; the ::before extends the hit area to 44x36.
        'relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-full border transition-colors duration-150 before:absolute before:-inset-x-[4px] before:-inset-y-[8px] before:content-[""] disabled:opacity-50',
        checked
          ? enforce
            ? 'border-transparent bg-accent'
            : 'border-transparent bg-primary'
          : 'border-control-border bg-surface-3',
      )}
    >
      <RSwitch.Thumb className="block size-[16px] translate-x-[1px] rounded-full bg-surface shadow-float transition-transform duration-150 data-[state=checked]:translate-x-[17px]" />
    </RSwitch.Root>
  );
  if (visibleLabel === undefined) return root;
  return (
    <span className="inline-flex items-center gap-2">
      {root}
      <label
        htmlFor={switchId}
        className={clsx('select-none text-[13px] font-medium text-ink', !disabled && 'cursor-pointer')}
      >
        {visibleLabel}
      </label>
    </span>
  );
}

/** Hover/focus tooltip. Wrap the app once in `TooltipProvider`. */
export function Tooltip({
  content,
  children,
  side,
}: {
  content: ReactNode;
  children: ReactNode;
  /** Preferred side (Radix), default `top`. */
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  return (
    <RTooltip.Root>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content
          side={side}
          sideOffset={6}
          className="z-50 max-w-xs rounded-md bg-primary px-2 py-1 text-[12px] font-medium text-on-primary shadow-elevated"
        >
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}

export const TooltipProvider = RTooltip.Provider;
